// src/services/api/endpoints/checkins.ts
import { AdminCheckInRequest, CheckInFullResponse, CheckInPaginatedResponse } from '../../../types';
import { API_BASE_URL, fetchWithTokenRefresh } from '../client';
import { 
  generateCacheKey,
  getCacheItem,
  setCacheItem,
  invalidateCache
} from '../cache';

/**
 * Admin check-in endpoint for checking in users from Space Ops dashboard
 * @param token Firebase authentication token
 * @param userId ID of the user to check in
 * @param spaceId ID of the space to check into
 * @param useDayPass Whether to force day pass usage even if membership quota available
 * @returns Promise containing check-in response with status and details
 */
export const adminCheckInUser = async (
  token: string,
  userId: string,
  spaceId: string,
  useDayPass: boolean = false
): Promise<CheckInFullResponse> => {
  try {
    const url = `${API_BASE_URL}/check-in/admin`;
    
    const requestBody: AdminCheckInRequest = {
      user_id: userId,
      space_id: spaceId,
      use_day_pass: useDayPass
    };

    // Make POST request with request body
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    // Invalidate user-related caches after successful check-in
    if (response.status === 'success') {
      // Invalidate user data cache to refresh usage information
      invalidateCache(`user-${userId}`);
      invalidateCache(`user-checkins-${userId}`);
      invalidateCache('users'); // Invalidate general users cache
      
      // Invalidate day passes cache if a day pass was used
      if (response.quota_type === 'day_pass') {
        const dayPassCacheKeys = [
          generateCacheKey(`${API_BASE_URL}/day-passes/users/${userId}/day-passes`, { userId, onlyActive: true }),
          generateCacheKey(`${API_BASE_URL}/day-passes/users/${userId}/day-passes`, { userId, onlyActive: false })
        ];
        dayPassCacheKeys.forEach(key => invalidateCache(key));
      }
    }

    return response;
  } catch (error) {
    console.error('Error during admin check-in:', error);
    
    // Return error response in expected format
    return {
      status: 'error',
      message: error instanceof Error ? error.message : 'An unexpected error occurred during check-in'
    };
  }
};

/**
 * Invalidate check-in related caches
 * @param userId Optional user ID to invalidate specific user caches
 */
export const invalidateCheckInCaches = (userId?: string): void => {
  // Invalidate general caches
  invalidateCache('users');
  
  if (userId) {
    // Invalidate user-specific caches
    invalidateCache(`user-${userId}`);
    invalidateCache(`user-checkins-${userId}`);
    
    // Invalidate day passes caches for the user
    const dayPassCacheKeys = [
      generateCacheKey(`${API_BASE_URL}/day-passes/users/${userId}/day-passes`, { userId, onlyActive: true }),
      generateCacheKey(`${API_BASE_URL}/day-passes/users/${userId}/day-passes`, { userId, onlyActive: false })
    ];
    dayPassCacheKeys.forEach(key => invalidateCache(key));
  }
};

/**
 * Search check-ins (admin-only)
 * Mirrors GET /check-in/search with filters for date range and optional space
 */
export const searchCheckIns = async (
  token: string,
  params: {
    spaceId?: string;
    fromDate?: string; // YYYY-MM-DD (optional - if not provided, fetches all)
    toDate?: string;   // YYYY-MM-DD (optional - if not provided, fetches all)
    page?: number;
    limit?: number;
    orderBy?: string; // default 'timestamp'
    orderDirection?: 'asc' | 'desc'; // default 'desc'
    bypassCache?: boolean;
  }
): Promise<CheckInPaginatedResponse> => {
  const {
    spaceId,
    fromDate,
    toDate,
    page = 1,
    limit = 100,
    orderBy = 'timestamp',
    orderDirection = 'desc',
    bypassCache = false,
  } = params;

  // Build query string using backend param names
  const query = new URLSearchParams();
  if (spaceId) query.set('space_id', spaceId);
  if (fromDate) query.set('from_date', fromDate);
  if (toDate) query.set('to_date', toDate);
  query.set('page', String(page));
  query.set('limit', String(limit));
  query.set('order_by', orderBy);
  query.set('order_direction', orderDirection);

  const url = `${API_BASE_URL}/check-in/search?${query.toString()}`;

  // Cache key should not include token or bypass flag
  const cacheKey = generateCacheKey(url, {
    space_id: spaceId,
    from_date: fromDate,
    to_date: toDate,
    page,
    limit,
    order_by: orderBy,
    order_direction: orderDirection,
  });

  if (!bypassCache) {
    const cached = getCacheItem<CheckInPaginatedResponse>(cacheKey);
    if (cached) return cached;
  }

  const response = await fetchWithTokenRefresh(url, token);
  // Use default TTL from cache implementation
  setCacheItem(cacheKey, response);
  return response;
};
